import { useCallback, useEffect, useRef, useState } from "react";
import { isTextInputTarget, resolveEnterAction } from "../model/commandeListRules";
import { useCommandeContextMenu } from "./useCommandeContextMenu";
import { useCommandeListFlags } from "./useCommandeListFlags";
import { useCommandeListRefresh } from "./useCommandeListRefresh";
import { useCommandePendingDeletes } from "./useCommandePendingDeletes";

function useListKeyboard({ commandes, focusedIndex, setFocusedIndex, selectedCommandeId, onEdit, handleRowClick, handleRefresh }) {
    return useCallback((e) => {
        if (isTextInputTarget(e.target)) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setFocusedIndex(i => Math.min(i + 1, commandes.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setFocusedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            const action = resolveEnterAction({ commandes, focusedIndex, selectedCommandeId, canEdit: Boolean(onEdit) });
            if (!action) return;
            e.preventDefault();
            if (action.type === "edit") onEdit(action.commande);
            if (action.type === "select") handleRowClick(action.commande, action.index);
        } else if ((e.key === "r" || e.key === "R") && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            handleRefresh();
        }
    }, [commandes, focusedIndex, setFocusedIndex, handleRefresh, handleRowClick, onEdit, selectedCommandeId]);
}

// État et actions de la liste des commandes : focus clavier, indicateurs, actualisation,
// menu contextuel et suppressions différées.
export function useCommandeListTable({ commandes, onSelect, onEdit, selectedCommandeId, onRefresh, onFlagChange }) {
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = useRef(null);
    const { getFlag, patchFlags } = useCommandeListFlags(commandes, onFlagChange);
    const { isRefreshing, handleRefresh } = useCommandeListRefresh(onRefresh);
    const { contextMenu, openContextMenu, closeContextMenu } = useCommandeContextMenu();
    const { pendingDeletes, requestDelete, cancelDelete, flushPendingDeletes } = useCommandePendingDeletes(onRefresh);

    useEffect(() => { setFocusedIndex(-1); }, [commandes]);

    const handleRowClick = useCallback((cmd, idx) => {
        flushPendingDeletes();
        containerRef.current?.focus({ preventScroll: true });
        setFocusedIndex(idx);
        if (!getFlag(cmd, "read")) {
            patchFlags(cmd.id, { read: true }, { read: getFlag(cmd, "read") });
        }
        onSelect(cmd);
    }, [flushPendingDeletes, getFlag, patchFlags, onSelect]);

    const handleStarClick = useCallback((e, cmd) => {
        e.stopPropagation();
        const currentFavorite = getFlag(cmd, "favorite");
        patchFlags(cmd.id, { favorite: !currentFavorite }, { favorite: currentFavorite });
    }, [getFlag, patchFlags]);

    const handleDelete = useCallback((cmd) => {
        closeContextMenu();
        requestDelete(cmd);
    }, [closeContextMenu, requestDelete]);

    const handleKeyDown = useListKeyboard({
        commandes, focusedIndex, setFocusedIndex, selectedCommandeId, onEdit, handleRowClick, handleRefresh,
    });

    return {
        containerRef,
        focusedIndex,
        getFlag,
        patchFlags,
        isRefreshing,
        handleRefresh,
        contextMenu,
        openContextMenu,
        closeContextMenu,
        pendingDeletes,
        handleDelete,
        cancelDelete,
        handleRowClick,
        handleStarClick,
        handleKeyDown,
    };
}
